import { useState } from "react";
import { Store, Handshake, Mic, CreditCard, Download, Loader2, Check, ShieldCheck, UploadCloud } from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Reveal, Badge } from "@/components/site/primitives";
import { IMAGES } from "@/lib/constants";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";

const field = "w-full bg-[#08080c] border border-white/15 rounded-lg px-4 py-3 outline-none focus:border-[#00f0ff] transition-colors";

const SPONSOR_TIERS = [
  {
    id: "sponsor_title", name: "Title Sponsor", price: 25000, accent: "#ff0055",
    includes: [
      "Exclusive naming rights — \"presented by [You]\"",
      "Headline logo on main-stage backdrop & step-and-repeat",
      "Top logo placement on website, tickets & all marketing",
      "10 VIP Spider-Society passes",
      "Dedicated on-stage shout-outs all night",
      "Premium activation booth in prime location",
      "Full-page feature in event program + social spotlight",
    ],
  },
  {
    id: "sponsor_corporate", name: "Corporate Sponsorship", price: 10000, accent: "#00f0ff",
    includes: [
      "Large logo on stage & website sponsor wall",
      "6 VIP Spider-Society passes",
      "10x10 branded activation booth",
      "Logo on printed program + social features",
      "Announced shout-outs during the event",
    ],
  },
  {
    id: "sponsor_community", name: "Community Partner", price: 4500, accent: "#ffe600",
    includes: [
      "Logo on website sponsor wall & program",
      "4 General Admission tickets",
      "Shared community activation table",
      "Social media thank-you post",
    ],
  },
  {
    id: "sponsor_digital", name: "Digital Sponsorship", price: 500, accent: "#00f0ff",
    includes: [
      "Your logo auto-displayed on our website sponsor wall",
      "Clickable link to your site from our sponsors section",
      "Social media shout-out",
      "Listed in the digital event program",
    ],
  },
];

const VENDOR_CATEGORIES = [
  "Comic Creator", "Printmaker / Artist", "Apparel & Streetwear",
  "Collectibles & Toys", "Food Truck", "Food & Beverage",
  "Jewelry & Accessories", "Crafts & Handmade", "Other",
];

function useSubmit() {
  const [loading, setLoading] = useState(false);
  const run = async (endpoint, payload, lookup_key) => {
    setLoading(true);
    try {
      const { data } = await api.post(endpoint, payload);
      if (lookup_key) {
        const { data: co } = await api.post("/payments/checkout", {
          lookup_key,
          origin_url: window.location.origin,
          submission_id: data.id,
        });
        window.location.href = co.checkout_url;
        return;
      }
      toast.success("Application received! We'll be in touch at " + payload.email);
      setLoading(false);
      return true;
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Something went wrong");
      setLoading(false);
    }
  };
  return { loading, run };
}

function VendorForm() {
  const [f, setF] = useState({ businessName: "", contactName: "", email: "", phone: "", portfolioLink: "", productCategory: "", otherCategory: "", notes: "" });
  const { loading, run } = useSubmit();
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));
  const submit = async (e) => {
    e.preventDefault();
    if (!f.productCategory) return toast.error("Select a product category");
    if (f.productCategory === "Other" && !f.otherCategory.trim()) return toast.error("Please name your category");
    const category = f.productCategory === "Other" ? f.otherCategory.trim() : f.productCategory;
    const { otherCategory, ...rest } = f;
    const ok = await run("/applications/vendor", { ...rest, productCategory: category }, "vendor_booth");
    if (ok) setF({ businessName: "", contactName: "", email: "", phone: "", portfolioLink: "", productCategory: "", otherCategory: "", notes: "" });
  };
  return (
    <form onSubmit={submit} className="space-y-4" data-testid="vendor-form">
      <div className="grid sm:grid-cols-2 gap-4">
        <input required placeholder="Business name" value={f.businessName} onChange={set("businessName")} className={field} data-testid="vendor-business" />
        <input required placeholder="Contact name" value={f.contactName} onChange={set("contactName")} className={field} data-testid="vendor-contact" />
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        <input required type="email" placeholder="Email" value={f.email} onChange={set("email")} className={field} data-testid="vendor-email" />
        <input placeholder="Phone (optional)" value={f.phone} onChange={set("phone")} className={field} />
      </div>
      <input placeholder="Portfolio / Instagram link" value={f.portfolioLink} onChange={set("portfolioLink")} className={field} data-testid="vendor-portfolio" />
      <Select value={f.productCategory} onValueChange={(v) => setF((s) => ({ ...s, productCategory: v }))}>
        <SelectTrigger data-testid="vendor-category" className="bg-[#08080c] border-white/15 h-12"><SelectValue placeholder="Product category" /></SelectTrigger>
        <SelectContent className="bg-[#101018] border-white/10 text-white">
          {VENDOR_CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
        </SelectContent>
      </Select>
      {f.productCategory === "Other" && (
        <input required placeholder="Name your category" value={f.otherCategory} onChange={set("otherCategory")} className={field} data-testid="vendor-other-category" />
      )}
      <textarea placeholder="Tell us about your booth (optional)" value={f.notes} onChange={set("notes")} rows={3} className={field} />
      <div className="glass rounded-xl p-4 text-sm text-white/70 font-head flex gap-3">
        <ShieldCheck size={20} className="text-[#00f0ff] shrink-0 mt-0.5" />
        <span>We place a <b className="text-white">$250 authorization hold</b> on your card now — you are <b className="text-white">not charged</b> until we approve your application. If you're declined, the hold is released automatically.</span>
      </div>
      <div className="flex items-center justify-between flex-wrap gap-4 pt-2">
        <div className="font-head"><span className="text-white/50 text-sm">Booth fee (held)</span><div className="font-display text-3xl text-[#ffe600]">$250.00</div></div>
        <button data-testid="vendor-pay-btn" disabled={loading} className="inline-flex items-center gap-2 bg-[#ff0055] text-white font-head font-bold uppercase px-8 py-3.5 rounded-full glow-magenta hover:scale-105 transition-transform disabled:opacity-60">
          {loading ? <Loader2 className="animate-spin" size={18} /> : <CreditCard size={18} />} Apply & Authorize $250
        </button>
      </div>
    </form>
  );
}

function readFileAsDataURL(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

function SponsorForm() {
  const [f, setF] = useState({ companyName: "", contactName: "", email: "", phone: "", tier: "", website: "", logo: "", message: "" });
  const [logoName, setLogoName] = useState("");
  const { loading, run } = useSubmit();
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));
  const tier = SPONSOR_TIERS.find((t) => t.name === f.tier);

  const onLogo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 600 * 1024) return toast.error("Logo must be under 600KB");
    const dataUrl = await readFileAsDataURL(file);
    setF((s) => ({ ...s, logo: dataUrl }));
    setLogoName(file.name);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!tier) return toast.error("Choose a sponsorship tier");
    await run("/applications/sponsor", f, tier.id);
  };
  return (
    <form onSubmit={submit} className="space-y-5" data-testid="sponsor-form">
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
        {SPONSOR_TIERS.map((t) => (
          <button
            key={t.id}
            type="button"
            data-testid={`sponsor-tier-${t.id}`}
            onClick={() => setF((s) => ({ ...s, tier: t.name }))}
            className={`text-left rounded-xl p-4 border transition-all ${f.tier === t.name ? "border-[#ff0055] chromatic-box" : "border-white/10 glass hover:border-white/30"}`}
          >
            <div className="font-head font-bold text-sm">{t.name}</div>
            <div className="font-display text-2xl mt-1" style={{ color: t.accent }}>${t.price.toLocaleString()}</div>
          </button>
        ))}
      </div>

      {tier && (
        <div className="glass rounded-xl p-5" data-testid="sponsor-includes">
          <div className="font-mono2 text-[10px] tracking-[0.25em] uppercase text-white/50 mb-3">What's included · {tier.name}</div>
          <ul className="grid sm:grid-cols-2 gap-x-6 gap-y-2">
            {tier.includes.map((inc) => (
              <li key={inc} className="flex gap-2 text-sm text-white/80 font-head">
                <Check size={16} style={{ color: tier.accent }} className="shrink-0 mt-0.5" /> {inc}
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="grid sm:grid-cols-2 gap-4">
        <input required placeholder="Company name" value={f.companyName} onChange={set("companyName")} className={field} data-testid="sponsor-company" />
        <input required placeholder="Contact name" value={f.contactName} onChange={set("contactName")} className={field} />
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        <input required type="email" placeholder="Email" value={f.email} onChange={set("email")} className={field} data-testid="sponsor-email" />
        <input placeholder="Phone (optional)" value={f.phone} onChange={set("phone")} className={field} />
      </div>
      <input placeholder="Website URL (shown with your logo)" value={f.website} onChange={set("website")} className={field} data-testid="sponsor-website" />

      <div>
        <label className="flex items-center gap-3 cursor-pointer glass rounded-xl p-4 hover:border-[#00f0ff]/50 border border-white/10">
          <UploadCloud size={22} className="text-[#00f0ff]" />
          <div className="flex-1 font-head text-sm">
            <div className="text-white">{logoName || "Upload your logo (PNG/SVG, under 600KB)"}</div>
            <div className="text-white/40 text-xs">Digital sponsors' logos auto-appear on our website sponsor wall.</div>
          </div>
          <input type="file" accept="image/*" onChange={onLogo} className="hidden" data-testid="sponsor-logo-input" />
        </label>
        {f.logo && <img src={f.logo} alt="logo preview" className="mt-3 h-14 rounded bg-white/90 p-2 object-contain" data-testid="sponsor-logo-preview" />}
      </div>

      <textarea placeholder="Partnership goals / message" value={f.message} onChange={set("message")} rows={3} className={field} />
      <div className="flex items-center gap-4 flex-wrap pt-1">
        <button data-testid="sponsor-pay-btn" disabled={loading} className="inline-flex items-center gap-2 bg-[#ff0055] text-white font-head font-bold uppercase px-8 py-3.5 rounded-full glow-magenta hover:scale-105 transition-transform disabled:opacity-60">
          {loading ? <Loader2 className="animate-spin" size={18} /> : <CreditCard size={18} />} {tier ? `Sponsor · $${tier.price.toLocaleString()}` : "Become a Sponsor"}
        </button>
        <a
          href="/sponsor-deck"
          data-testid="sponsor-deck-btn"
          className="inline-flex items-center gap-2 glass-chroma font-head font-bold uppercase px-6 py-3.5 rounded-full hover:scale-105 transition-transform"
        >
          <Download size={18} /> Sponsor Deck (PDF)
        </a>
      </div>
    </form>
  );
}

function PerformerForm() {
  const [f, setF] = useState({ name: "", email: "", phone: "", discipline: "", mediaLink: "", bio: "" });
  const { loading, run } = useSubmit();
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));
  const submit = async (e) => {
    e.preventDefault();
    if (!f.discipline) return toast.error("Select your discipline");
    const ok = await run("/applications/performer", f);
    if (ok) setF({ name: "", email: "", phone: "", discipline: "", mediaLink: "", bio: "" });
  };
  return (
    <form onSubmit={submit} className="space-y-4" data-testid="performer-form">
      <div className="grid sm:grid-cols-2 gap-4">
        <input required placeholder="Artist / Stage name" value={f.name} onChange={set("name")} className={field} data-testid="performer-name" />
        <input required type="email" placeholder="Email" value={f.email} onChange={set("email")} className={field} data-testid="performer-email" />
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        <input placeholder="Phone (optional)" value={f.phone} onChange={set("phone")} className={field} />
        <Select value={f.discipline} onValueChange={(v) => setF((s) => ({ ...s, discipline: v }))}>
          <SelectTrigger data-testid="performer-discipline" className="bg-[#08080c] border-white/15 h-12"><SelectValue placeholder="Discipline" /></SelectTrigger>
          <SelectContent className="bg-[#101018] border-white/10 text-white">
            {["DJ / Producer", "Breakdancer", "Graffiti Muralist", "Cosplayer", "Acrobat / Stunt", "Other"].map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <input placeholder="Portfolio / media link (SoundCloud, IG, YouTube)" value={f.mediaLink} onChange={set("mediaLink")} className={field} data-testid="performer-media" />
      <textarea placeholder="Short bio" value={f.bio} onChange={set("bio")} rows={3} className={field} />
      <button data-testid="performer-submit-btn" disabled={loading} className="inline-flex items-center gap-2 bg-[#00f0ff] text-black font-head font-bold uppercase px-8 py-3.5 rounded-full glow-cyan hover:scale-105 transition-transform disabled:opacity-60">
        {loading ? <Loader2 className="animate-spin" size={18} /> : <Mic size={18} />} Submit for Consideration
      </button>
    </form>
  );
}

export default function GetInvolved() {
  return (
    <div className="pt-24 md:pt-32">
      <section className="relative overflow-hidden">
        <img src={IMAGES.streetwear} alt="" className="absolute inset-0 w-full h-full object-cover opacity-20" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#08080c]/60 to-[#08080c]" />
        <div className="relative max-w-7xl mx-auto px-5 md:px-8 text-center py-10">
          <Badge color="cyan">Join The Multiverse</Badge>
          <h1 className="font-display text-6xl md:text-8xl uppercase mt-4">Get <span className="chromatic">Involved</span></h1>
          <p className="text-white/60 font-head max-w-xl mx-auto mt-4">
            Vendors, sponsors &amp; performers — help us build the wildest all-ages Spider-Verse in Denver.
          </p>
        </div>
      </section>

      <section className="max-w-4xl mx-auto px-5 md:px-8 py-14">
        <Tabs defaultValue="vendor">
          <TabsList data-testid="get-involved-tabs" className="grid grid-cols-3 w-full bg-[#101018] border border-white/10 rounded-full p-1 h-auto">
            <TabsTrigger value="vendor" data-testid="tab-vendor" className="rounded-full py-2.5 font-head uppercase text-xs md:text-sm data-[state=active]:bg-[#ff0055] data-[state=active]:text-white gap-2"><Store size={16} /> Vendor</TabsTrigger>
            <TabsTrigger value="sponsor" data-testid="tab-sponsor" className="rounded-full py-2.5 font-head uppercase text-xs md:text-sm data-[state=active]:bg-[#ff0055] data-[state=active]:text-white gap-2"><Handshake size={16} /> Sponsor</TabsTrigger>
            <TabsTrigger value="performer" data-testid="tab-performer" className="rounded-full py-2.5 font-head uppercase text-xs md:text-sm data-[state=active]:bg-[#ff0055] data-[state=active]:text-white gap-2"><Mic size={16} /> Performer</TabsTrigger>
          </TabsList>

          <div className="glass-chroma rounded-3xl p-6 md:p-10 mt-6">
            <TabsContent value="vendor" className="mt-0">
              <h2 className="font-display text-3xl md:text-4xl uppercase mb-1">Vendor Application</h2>
              <p className="text-white/50 font-head text-sm mb-6">Comic creators, printmakers &amp; apparel makers welcome.</p>
              <VendorForm />
            </TabsContent>
            <TabsContent value="sponsor" className="mt-0">
              <h2 className="font-display text-3xl md:text-4xl uppercase mb-1">Sponsor Partnerships</h2>
              <p className="text-white/50 font-head text-sm mb-6">Put your brand at the center of the multiverse.</p>
              <SponsorForm />
            </TabsContent>
            <TabsContent value="performer" className="mt-0">
              <h2 className="font-display text-3xl md:text-4xl uppercase mb-1">Performer & Artist Submissions</h2>
              <p className="text-white/50 font-head text-sm mb-6">DJs, breakdancers, muralists &amp; cosplayers.</p>
              <PerformerForm />
            </TabsContent>
          </div>
        </Tabs>
      </section>
    </div>
  );
}
